/* iceberg-ui */
import { IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Box, Button, CircularProgress, Typography } from '@mui/material';

/*  Shared  */
import { DarkTooltip } from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';
import { hmsFormatter } from 'application/Shared/Helpers';

/* Hooks */
import { useSession } from 'hooks/redux/Selectors/session';

/* Hooks */
import { useTenantInfoByTenantId } from 'hooks/redux/Selectors/tenants';
import { TFunction } from 'i18next';

/*  React  */
import React, { useEffect, useRef, useState } from 'react';
// @ts-expect-error Could not find a declaration file for module 'react-csv'.
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/* Internal Components */
import { useManagedTableV2Context } from '../ManagedTableV2ContextProvider';
import { CSVColumn, CSVTableContext } from '../types';

interface ManagedTableCSVExportV2Props<TData> {
    csvBuildOnRequest: boolean;
    csvData: TData[] | undefined;
    csvExport: boolean;
    csvExportFields: CSVColumn[];
    csvExportFileName: string;
    csvIconOnly: boolean;
    disabled: boolean;
    maxExportRows: number;
}

/*  Constants  */
const DEFAULT_MAX_EXPORT_ROWS = 40000;

const transformString = (name: string | undefined) => {
    if (!name) return '';

    return name
        .trim()
        .replaceAll(' ', '_')
        .toLowerCase()
        .replaceAll(/(\.|,)/g, '');
};

const getCsvExportCustomerName = ({
    clientName,
    partnerName
}: {
    clientName: string | undefined;
    partnerName: string | undefined;
}) => {
    const partner = transformString(partnerName);
    const client = transformString(clientName);

    if (!partner && !client) return '';

    if (!client) {
        return partner;
    }

    if (!partner) {
        return client;
    }

    return `${partner}-${client}`;
};

const getCsvFilename = ({
    clientName,
    csvExportFileName,
    partnerName,
    t
}: {
    clientName: string | undefined;
    csvExportFileName: string;
    partnerName: string | undefined;
    t: TFunction<'translation', undefined>;
}) => {
    const csvExportCustomerName = getCsvExportCustomerName({
        clientName,
        partnerName
    });

    let csvFilename =
        csvExportCustomerName !== '' ? `${csvExportCustomerName}-` : '';
    csvFilename += `${t('Common.report')}-${csvExportFileName}-`;
    csvFilename += `${new Date().getFullYear().toString().padStart(2, '0')}-`;
    csvFilename += `${(new Date().getMonth() + 1)
        .toString()
        .padStart(2, '0')}-`;
    csvFilename += `${new Date().getDate().toString().padStart(2, '0')}_`;
    csvFilename += `${new Date().getHours().toString().padStart(2, '0')}-`;
    csvFilename += `${new Date().getMinutes().toString().padStart(2, '0')}_`;
    csvFilename += new Date().getSeconds().toString().padStart(2, '0');
    return csvFilename;
};

export const ManagedTableCSVExportV2 = <TData,>({
    csvBuildOnRequest,
    csvData,
    csvExport,
    csvExportFields: csvExportFieldsProp,
    csvExportFileName,
    csvIconOnly,
    disabled,
    maxExportRows
}: ManagedTableCSVExportV2Props<TData>) => {
    const { t } = useTranslation();
    const { clientId, partnerId } = useParams();
    const session = useSession();
    const { dataAPI, rowCount, tableName, table } =
        useManagedTableV2Context() as CSVTableContext<TData>;
    const rowData = table.getFilteredRowModel().rows.map((row) => row.original);

    const partnerTenantInfo = useTenantInfoByTenantId(partnerId);
    const clientTenantInfo = useTenantInfoByTenantId(clientId);

    const partnerName = partnerTenantInfo?.tenant?.name;
    const clientName = clientTenantInfo?.tenant?.name;

    const [csvReady, setCsvReady] = useState(true);
    const [csvFilename, setCsvFilename] = useState('');
    const [exportData, setExportData] = useState<
        Record<string, unknown>[] | null
    >(null);
    const csvInstance = useRef<CSVLink>(null);
    const visibleColumns = table.getVisibleFlatColumns();

    // Filter the provided CSV export fields to include only those corresponding to visible columns
    const csvExportFields = csvExportFieldsProp.filter((field) => {
        const { id } = field;
        // This logic depends on adding ids to the CSV export fields we want to conditionally include.
        // Include fields without id (always included) or fields whose id matches visible columns.
        return visibleColumns.find((column) => !id || column.id === id);
    });

    // Deal with https://github.com/react-csv/react-csv/issues/189
    useEffect(() => {
        if (exportData && csvInstance.current && csvInstance.current.link) {
            setTimeout(() => {
                csvInstance.current.link.click();
                setExportData(null);
            });
        }
    }, [exportData]);

    const preExportCSV = async () => {
        let currentExportData: Record<string, unknown>[] = csvData as Record<
            string,
            unknown
        >[];
        if (!csvData && csvBuildOnRequest) {
            dataAPI.addParameter('csv', true);
            const response = await dataAPI.getResults();

            currentExportData = response;
        } else if (!csvData) {
            currentExportData = JSON.parse(JSON.stringify(rowData));
        }

        currentExportData = currentExportData.map((r) => {
            const row = r;

            csvExportFields.forEach((field) => {
                const { key, transform } = field;

                if (
                    !row[key] &&
                    transform !== 'listExtract' &&
                    transform !== 'objectExtract' &&
                    transform !== 'custom'
                ) {
                    return;
                }

                // If transform is listExtract, val is undefined because row.key does not exist yet.
                // key will be a new property of row to be set below.
                let val = row[key];

                // Convert Object to JSON
                if (typeof val === 'object' && transform !== 'objectExtract') {
                    const stringifiedVal = JSON.stringify(val);
                    row[key] = stringifiedVal.replace(/"/g, '""');
                    return;
                }

                if (transform === 'hms') {
                    val = parseInt(val as string, 10);
                    if (val === 0 || Number.isNaN(val)) {
                        row[key] = '-';
                        return;
                    }

                    row[key] = hmsFormatter(val as number);
                }

                if (transform === 'bool_YN') {
                    row[key] = val ? 'Y' : 'N';
                }

                /*
                    listExtract is used to extract a list of strings from an array.
                    The `key` parameter for this transform is the name of the new property to be added to the row
                    and should be different than the original key where the data is found.

                    The `rowKeyAndDataPath` parameter is used to determine how to extract the data from the array.
                    It can handle an array of strings or an array of objects (even deeply nested ones).
                    rowKeyAndDataPath follows the pattern of `keyToData:path.to.data`.
                    Here are some examples.

                    For `rowKeyAndDataPath: 'aliases'`, `aliases` would be the key to find the data from the row
                    and there is no path, so the array is already just strings, and they are joined.
                        Example:
                            row input: `{ aliases: ['user1', 'user2'] }`
                            rowKeyAndDataPath: 'aliases'
                            output: 'user1, user2'

                    For `rowKeyAndDataPath: 'users:name'`,
                    `users` would be the key, and `name` would be the path in each object.
                        Example:
                            row input: `{ users: [{ name: 'user1' }, { name: 'user2' }] }`
                            rowKeyAndDataPath: 'users:name'
                            output: 'user1, user2'

                    For `rowKeyAndDataPath: 'browsers:application.name'` (nested objects)
                        `browsers` would be the key, and `application` and `name` would be the paths to find the nested strings.
                        Example:
                            row input: `{ browsers: [{ application: { name: 'chrome' } }, { application: { name: 'firefox' } }] }`
                            rowKeyAndDataPath: 'browsers:application.name'
                            output: 'chrome, firefox'
                */
                if (
                    transform === 'listExtract' &&
                    'rowKeyAndDataPath' in field
                ) {
                    const [rowKey, possiblePathKeys] =
                        field.rowKeyAndDataPath.split(':');

                    const pathKeys = possiblePathKeys
                        ? possiblePathKeys.split('.')
                        : [];

                    row[key] = '';
                    if (row[rowKey] && Array.isArray(row[rowKey])) {
                        row[key] = row[rowKey];
                        if (pathKeys.length) {
                            // for each row, we need to iterate over EACH path key to extract the final string value.
                            // if pathKey = ['name'] => { name: 'ian'}
                            // if pathKey = ['application', 'name'] => { application:  { name: 'chrome' } }
                            row[key] = (
                                row[key] as Array<Record<string, unknown>>
                            ).map((obj) =>
                                pathKeys.reduce(
                                    (
                                        acc: Record<string, unknown>,
                                        pathKey: string
                                    ) =>
                                        acc?.[pathKey] as Record<
                                            string,
                                            unknown
                                        >,
                                    obj
                                )
                            );
                        }

                        row[key] = (row[key] as Array<string>)
                            .filter(Boolean) // just in case any names are falsy, let's remove them.
                            .join(', ');
                    }
                }

                /*
                    objectExtract is used to extract values from a nested object. The `key` parameter for this transform
                    is the name of the new property to be added to the row and should be different than the
                    original key where the data is found. The `rowKeyAndDataPath` parameter is used to determine
                    how to extract the data from the object. It follows the pattern of `keyToData:path.to.data`.
                    The `rowKeyAndDataPath` can be a comma-seperated value which allows you to concatanate multiple
                    values from the same object.

                    Examples:

                    1. Single nested value:
                    rowKeyAndDataPath: 'user:email'
                    row input: { user: { email: 'user1@example.com' } }
                    output: 'user1@example.com'

                    2. Multiple values from same level:
                    rowKeyAndDataPath: 'user:firstName,lastName'
                    separator: ' ',
                    row input: { user: { firstName: 'John', lastName: 'Doe' } }
                    output: 'John Doe'

                    3. Multiple deeply nested values:
                    rowKeyAndDataPath: 'user:contact.email,details.phone'
                    row input: { user: { contact: { email: 'user1@example.com' }, details: { phone: '000000000000' } } }
                    output: 'user1@example.com, 000000000000'
                */
                if (
                    transform === 'objectExtract' &&
                    'rowKeyAndDataPath' in field
                ) {
                    const [rowKey, possiblePathKeys] =
                        field.rowKeyAndDataPath.split(':');
                    const paths = possiblePathKeys.split(',');

                    row[key] = paths
                        .map((path) => {
                            const pathKeys = path.split('.');
                            return pathKeys.reduce(
                                (
                                    acc: Record<string, unknown>,
                                    pathKey: string
                                ) => acc?.[pathKey] as Record<string, unknown>,
                                row[rowKey] as Record<string, unknown>
                            );
                        })
                        .filter(Boolean)
                        .join(field.separator);
                }

                if (
                    transform === 'custom' &&
                    'rowKeyAndDataPath' in field &&
                    'fn' in field
                ) {
                    if (!field.rowKeyAndDataPath || !field.fn) {
                        return;
                    }
                    row[key] = field.fn(row[field.rowKeyAndDataPath]);
                }

                if (typeof val === 'string') {
                    // Escape existing quotes by doubling them
                    val = val.replace(/"/g, '""');
                    row[key] = val;
                }
            });

            return row;
        });

        // Set CSV Filename
        setCsvFilename(
            getCsvFilename({
                clientName,
                csvExportFileName,
                partnerName,
                t
            })
        );
        setExportData(currentExportData);
    };

    const onDownloadCSVClick = async () => {
        setCsvReady(false);
        await preExportCSV();
        setCsvReady(true);
    };

    if (!csvExport) {
        return null;
    }

    // We only care about max export rows if the CSV data is being generated by a separate API call on the backend.
    const maxExportRowsExceeded =
        csvBuildOnRequest &&
        // If maxExportRows is passed as a prop and is > 0, use that value. Otherwise use the default constant.
        (maxExportRows > 0
            ? rowCount > maxExportRows
            : rowCount > DEFAULT_MAX_EXPORT_ROWS);

    const disableExport =
        rowCount === 0 ||
        (!session?.sessionSettings?.expiredTrial &&
            session?.sessionSettings?.inTrial) ||
        maxExportRowsExceeded;

    let tooltipTitle = '';

    if (rowCount === 0 || !disableExport) {
        tooltipTitle = '';
    } else if (maxExportRowsExceeded) {
        tooltipTitle = t('Tooltips.exportFunctionality.disabled_max_rows');
    } else {
        tooltipTitle = t('Tooltips.exportFunctionality.disabled');
    }

    return (
        <Box
            display="flex"
            alignItems="center"
            data-testid="managedTableCsvExport"
            className="no-print"
        >
            <DarkTooltip arrow title={tooltipTitle}>
                <Box
                    display="flex"
                    alignItems="center"
                    data-testid="managedTableCsvExportBtnBox"
                >
                    <Button
                        data-trackid={`managedTableCsvExportV2Btn_${tableName}`}
                        color="primary"
                        data-testid="managedTableCsvExportBtn"
                        disabled={
                            disabled || Boolean(disableExport) || !csvReady
                        }
                        onClick={() => onDownloadCSVClick()}
                        size="anmSmall"
                        variant="outlined"
                    >
                        {csvIconOnly && (
                            <IconWithVariant
                                src="Csv"
                                size={FontSizes[1]}
                                wrapperProps={{ padding: '4px' }} // Padding to match height of nearby buttons
                            />
                        )}
                        {!csvIconOnly && (
                            <Typography>{t('Common.export')}</Typography>
                        )}
                    </Button>
                    {!csvReady && (
                        <CircularProgress
                            size="24px"
                            sx={{ margin: '4px', marginLeft: '8px' }}
                        />
                    )}
                </Box>
            </DarkTooltip>
            {exportData && (
                <CSVLink
                    asyncOnClick
                    data={exportData}
                    filename={csvFilename}
                    headers={csvExportFields}
                    ref={csvInstance}
                />
            )}
        </Box>
    );
};
